/**
 * DEPLOY-FRESHNESS-001 — pick a new build up on the next navigation, no click needed.
 *
 * While an update is pending (lib/appRelease.ts), the next in-app navigation becomes a
 * full page load to the same target: nothing on the current screen is lost (the user
 * is leaving it anyway) and the destination renders on the new build. Rate-limited by
 * HARD_NAVIGATION_COOLDOWN_MS so a rolling deploy (two pods, two builds for a minute)
 * cannot bounce the user between builds — after one full load the banner takes over.
 *
 * Navigation also drives a throttled manifest check, so an active user learns about a
 * deploy within NAVIGATION_CHECK_THROTTLE_MS instead of waiting for the 5-minute poll.
 */
import { START_LOCATION, type Router } from 'vue-router'
import {
  type FetchLike,
  hardNavigationExecutor,
  hardNavigationHref,
  isNavigationCheckDue,
  markHardNavigation,
  refreshReleaseState,
  releaseState,
  shouldHardNavigate
} from '@/lib/appRelease'

export function installReleaseNavigationGuard(
  router: Router,
  options: { fetchImpl?: FetchLike; now?: () => number } = {}
): void {
  const now = options.now ?? (() => Date.now())
  const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init))

  router.beforeEach((to, from) => {
    // Same URL (query/hash tweaks, redirects to self) never forces a full load.
    if (to.fullPath === from.fullPath) return true
    const at = now()
    if (!shouldHardNavigate(releaseState, at)) return true
    markHardNavigation(at)
    hardNavigationExecutor.execute(hardNavigationHref(to.fullPath, router.options.history.base))
    return false
  })

  router.afterEach((_to, from) => {
    // Boot navigation: the App watcher checks on mount — no second request here.
    if (from === START_LOCATION) return
    const at = now()
    if (!isNavigationCheckDue(releaseState, at)) return
    void refreshReleaseState(fetchImpl, at)
  })
}
